import { useEffect, useState } from "react";
import { Reveal } from "../components/Reveal";
import { api } from "../lib/api";
import { useModal } from "../context/ModalContext";
import { Plus, Minus } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";

export function Faq() {
  const [faqs, setFaqs] = useState([]);
  const [openId, setOpenId] = useState(null);
  const { open } = useModal();
  useEffect(() => { api.get("/faqs").then((r) => setFaqs(r.data)).catch(() => {}); }, []);
  useEffect(() => {
    document.querySelectorAll('script[data-faq-jsonld="true"]').forEach((node) => node.remove());
    if (!faqs.length) return undefined;
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.setAttribute("data-faq-jsonld", "true");
    script.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: faqs.map((faq) => ({
        "@type": "Question",
        name: faq.q,
        acceptedAnswer: { "@type": "Answer", text: faq.a },
      })),
    });
    document.head.appendChild(script);
    return () => script.remove();
  }, [faqs]);

  return (
    <section id="faq" className="section-pad bg-neutral-50" data-testid="faq-section">
      <div className="mx-auto max-w-4xl px-4 sm:px-6">
        <Reveal className="text-center">
          <span className="overline">FAQ</span>
          <h2 className="mt-3 font-heading text-4xl sm:text-5xl font-extrabold tracking-tight">
            Questions? <span className="brand-text">We've got answers</span>
          </h2>
        </Reveal>

        <div className="mt-10 space-y-3">
          {faqs.map((f, i) => {
            const isOpen = openId === f.id;
            return (
              <Reveal key={f.id} delay={Math.min(i, 6) * 0.03}>
                <div className={`card-soft overflow-hidden transition-shadow ${isOpen ? "shadow-soft-lg" : ""}`}>
                  <button
                    className="w-full flex items-center justify-between gap-4 text-left p-5"
                    onClick={() => setOpenId(isOpen ? null : f.id)}
                    data-testid={`faq-${f.id}`}
                    aria-expanded={isOpen}
                  >
                    <span className="font-heading font-semibold text-neutral-900">{f.q}</span>
                    <span className={`shrink-0 w-8 h-8 rounded-full flex items-center justify-center transition-colors ${isOpen ? "brand-gradient text-white" : "bg-neutral-100 text-neutral-600"}`}>
                      {isOpen ? <Minus size={16} /> : <Plus size={16} />}
                    </span>
                  </button>
                  <AnimatePresence initial={false}>
                    {isOpen && (
                      <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.3 }}>
                        <p className="px-5 pb-5 text-sm text-neutral-600 leading-relaxed">{f.a}</p>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </Reveal>
            );
          })}
        </div>

        <div className="text-center mt-10">
          <p className="text-neutral-500">Still have a question?</p>
          <button className="btn-primary mt-3" onClick={() => open("callback")} data-testid="faq-callback-btn">Request a Callback</button>
        </div>
      </div>
    </section>
  );
}
